import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from '../Link';
import { asPromoLocale, taxIcebergHref, taxIcebergLinkLabel } from '../../utils/salary/tax-iceberg-promo';

/**
 * Inline widget for anywhere a salary figure is shown: a small pill that opens the calculator
 * preset to that salary ("What does €4,000 a month leave you?"). Pass the monthly gross; it is
 * rounded to the nearest €50 for a clean, indexable URL. Without an amount it links to the
 * calculator's default view.
 */
export function TaxIcebergLink({
  grossMonthly,
  locale,
  className = '',
  size = 'sm',
}: {
  grossMonthly?: number | null;
  locale?: string;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const intl = useIntl();
  const l = asPromoLocale(locale ?? intl.locale);
  const amount =
    grossMonthly && grossMonthly > 0
      ? intl.formatNumber(Math.round(grossMonthly / 50) * 50, {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        })
      : null;
  return (
    <Link
      href={taxIcebergHref(l, grossMonthly ?? undefined)}
      locale={l}
      className={`inline-flex items-center gap-1.5 rounded-jc-control border border-jc-border-control bg-jc-surface font-semibold text-jc-ink transition-colors hover:border-jc-ink hover:bg-jc-elevated ${
        size === 'md' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs'
      } ${className}`}
    >
      <svg viewBox="0 0 20 20" className={size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'} aria-hidden="true">
        <path d="M2 11h16" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
        <path d="M6.5 11 9 4l2.2 3 1.3-1.5L14 11Z" fill="currentColor" opacity="0.9" />
        <path d="M5 11h10.5l-1.5 4.5L10 18l-4-2.2Z" fill="currentColor" opacity="0.45" />
      </svg>
      <span>{taxIcebergLinkLabel(l, amount)}</span>
      <span aria-hidden="true">→</span>
    </Link>
  );
}
